export const construirPartidasConDetalle = (
  partidasPayload,
  detallesPayload,
) => {
  const partidas = partidasPayload?.data ?? [];
  const detalles = detallesPayload?.data ?? [];

  const normalizarPartidaId = (value) => Number(value);
  const normalizarSubId = (value) =>
    value === null || value === undefined ? null : Number(value);

  const key = (partidaId, subpartidaId) =>
    `${normalizarPartidaId(partidaId)}::${normalizarSubId(subpartidaId)}`;

  const detallesValidos = detalles.filter(
    (d) => d?.roW_TYPE === 3 && normalizarPartidaId(d?.partidA_ID) > 0,
  );

  // Guardar 1 detalle por clave (si hay más, conserva el primero)
  const detallePorClave = detallesValidos.reduce((mapa, det) => {
    const k = key(det.partidA_ID, det.subpartidA_ID);
    if (!mapa.has(k)) mapa.set(k, det);
    return mapa;
  }, new Map());

  const subPartidasPorPartida = partidas.reduce((mapa, item) => {
    if (normalizarSubId(item.subpartidA_ID) === null) return mapa;

    const partidaId = normalizarPartidaId(item.partidA_ID);
    const lista = mapa.get(partidaId) ?? [];

    lista.push({
      ...item,
      detalle:
        detallePorClave.get(key(item.partidA_ID, item.subpartidA_ID)) ?? null,
    });

    mapa.set(partidaId, lista);
    return mapa;
  }, new Map());

  const principales = partidas
    .filter((item) => normalizarSubId(item.subpartidA_ID) === null)
    .map((principal) => {
      const partidaId = normalizarPartidaId(principal.partidA_ID);

      return {
        ...principal,
        detalle: detallePorClave.get(key(principal.partidA_ID, null)) ?? null,
        sub_partidas: subPartidasPorPartida.get(partidaId) ?? [],
      };
    });

  return {
    ...partidasPayload,
    data: principales,
  };
};

export const formatNumber = (value, min = 2, max = 2) => {
  if (value === null || value === undefined || value === "") return "";
  const numberValue = Number(value);
  if (Number.isNaN(numberValue)) return "";
  return numberValue.toLocaleString("es-CL", {
    minimumFractionDigits: min,
    maximumFractionDigits: max,
  });
};

export const formatNumberAnglo = (value, min = 2, max = 2) => {
  if (value === null || value === undefined || value === "") return "";
  const numberValue = Number(value);
  if (Number.isNaN(numberValue)) return "";
  return numberValue.toLocaleString("en-US", {
    minimumFractionDigits: min,
    maximumFractionDigits: max,
  });
};

export const toNumber = (value) =>
  value == null || Number.isNaN(Number(value)) ? 0 : Number(value);

export const formatDateYMD = (value) => {
  if (!value) return "";

  if (typeof value === "string") {
    if (value.includes("T")) return value.split("T")[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const formatWithMonthAsText = (value) => {
  if (!value) return "";

  const meses = [
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre",
  ];

  let year = "";
  let month = "";

  // Soporta "YYYY-MM" o "YYYY/MM"
  if (/^\d{4}[-/]\d{1,2}$/.test(value)) {
    [year, month] = value.split(/[-/]/);
  } else {
    // Soporta fecha completa (ej: "2026-06-20T00:00:00")
    const ymd = formatDateYMD(value);
    if (!ymd) return "";
    [year, month] = ymd.split("-");
  }

  const monthIndex = Number(month) - 1;
  if (monthIndex < 0 || monthIndex > 11) return value;

  return `${meses[monthIndex]} ${year}`;
};

export const getHashedRows = (detalles) => {
  return (detalles?.data ?? [])
    .filter((d) => String(d?.listadO_PARTIDA_CODIGO ?? "").trim() === "#")
    .map((d, idx) => {
      const montoPeriodo = toNumber(d?.eP_MES_MONTO_PAGO_MC);
      const montoAcumulado = toNumber(d?.acuM_MONTO_PAGO_MC);

      return {
        id: "hash-" + idx,
        partidA_ID: "hash-" + idx,
        subpartidA_ID: null,
        codigo: "",
        nombre:
          d?.partidA_CLASE_NEMO ??
          d?.listadO_PARTIDA_CLASE ??
          "Partidas no vigentes",
        unidad: d?.montO_TOTCONT_UNIDAD ?? "",
        detalle: {
          ...d,
          acuM_MONTO_PAGO_MC: montoAcumulado - montoPeriodo,
          eP_MES_MONTO_PAGO_MC: 0,
          eP_MES_MONTO_BASE_MC: 0,
        },
        sub_partidas: [],
      };
    });
};

export const getIncrementosDeduccionesCalculados = (detalles, incrementos) => {
  const detallesRows = (detalles?.data ?? []).filter(
    (d) =>
      String(d?.listadO_PARTIDA_CODIGO ?? "").trim() === "ª" &&
      d?.roW_TYPE === 3,
  );

  const incrementosRows = incrementos?.data ?? [];

  const prevIncById = new Map();
  const prevDedById = new Map();

  detallesRows.forEach((d) => {
    const comp = String(d?.dI_ID_COMPUESTA ?? "").trim();
    const isInc = comp.startsWith("I|");
    const isDed = comp.startsWith("D|");

    if (!isInc && !isDed) return;

    const id = isInc
      ? toNumber(d?.edP_INCREMENTO_ID)
      : toNumber(d?.edP_DEDUCCION_ID);

    if (!id) return;

    const row = {
      id,
      nombre: d?.partidA_CLASE_NEMO ?? "",
      ama: toNumber(d?.acuM_ANTERIOR_MONTO_PAGO_MC),
    };

    if (isInc) prevIncById.set(id, row);
    if (isDed) prevDedById.set(id, row);
  });

  const curIncById = new Map();
  const curDedById = new Map();

  incrementosRows.forEach((r) => {
    const id = toNumber(r?.id);
    if (!id) return;

    const row = {
      id,
      nombre: r?.nombre ?? "",
      pa: toNumber(r?.monto), // mes actual
    };

    if (r?.tipo === 2) curIncById.set(id, row);
    if (r?.tipo === 1) curDedById.set(id, row);
  });

  const buildRows = (prevMap, curMap) => {
    const ids = [
      ...Array.from(curMap.keys()),
      ...Array.from(prevMap.keys()).filter((id) => !curMap.has(id)),
    ];

    return ids.map((id) => {
      const prev = prevMap.get(id);
      const cur = curMap.get(id);

      const ama = toNumber(prev?.ama);
      const pa = toNumber(cur?.pa);
      return {
        id,
        nombre: cur?.nombre || prev?.nombre || `ID ${id}`,
        ama,
        pa,
        ta: ama + pa,
      };
    });
  };

  const incRows = buildRows(prevIncById, curIncById);
  const dedRows = buildRows(prevDedById, curDedById);

  const sum = (arr, key) => arr.reduce((acc, r) => acc + toNumber(r?.[key]), 0);

  const incAma = sum(incRows, "ama");
  const incPa = sum(incRows, "pa");
  const incTa = sum(incRows, "ta");

  const dedAma = sum(dedRows, "ama");
  const dedPa = sum(dedRows, "pa");
  const dedTa = sum(dedRows, "ta");

  return {
    incRows,
    dedRows,
    incAma,
    incPa,
    incTa,
    dedAma,
    dedPa,
    dedTa,
    subAma: incAma - dedAma,
    subPa: incPa - dedPa,
    subTa: incTa - dedTa,
  };
};
